import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import PhoneInput from "react-phone-input-2";
import InputMask from "react-input-mask";
import { toast } from "react-toastify";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import "react-phone-input-2/lib/style.css";

const emptyForm = {
  name: "",
  nickname: "",
  email: "",
  password: "",
  telephone: "",
  address: "",
  city: "",
  state: "",
  zipcode: "",
  birthday: "",
  gender: "",
  type: "",
  // Enviados com default, não exibidos no formulário de criação
  graduation: "",
  course: "",
  hometown: "",
  instagram: "",
  facebook: "",
  linkedin: "",
  admin: "0",
};

export default function NewMemberModal({ open, onClose, onSuccess }) {
  const { token } = useAuth();
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData(emptyForm);
      setIsClosing(false);
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => document.body.classList.remove("modal-open");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => { if (e.key === "Escape") handleClose(); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nickname || !formData.password) {
      toast.error("Apelido e senha são obrigatórios.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...formData,
        telephone: formData.telephone ? formData.telephone.replace(/[^\d+]/g, "") : "",
      };
      await api.post("/api/member-register/", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Membro cadastrado com sucesso!");
      onSuccess();
      handleClose();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error("Já existe um membro com esse apelido.");
      } else {
        toast.error("Erro ao cadastrar membro. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!open && !isClosing) return null;

  return createPortal(
    <div
      className={`new-member-modal${isClosing ? " closing" : ""}`}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="modal-backdrop" />
      <div className="new-member-modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Novo Membro</h5>
            <button type="button" className="btn-close" onClick={handleClose} aria-label="Fechar" />
          </div>
          <div className="modal-body">
            <form id="new-member-form" onSubmit={handleSubmit} className="profile-form" autoComplete="off">
              <label className="form-label">Tipo *</label>
              <select name="type" value={formData.type} onChange={handleChange} className="formsInside" style={{ height: "auto" }} required>
                <option value="">Selecione um tipo</option>
                <option value="Ex-Aluno">Ex-Aluno</option>
                <option value="Homenageado">Homenageado</option>
                <option value="Morador">Morador</option>
                <option value="Bixo">Bixo</option>
              </select>

              <label className="form-label">Nome *</label>
              <input type="text" name="name" placeholder="Nome" value={formData.name} onChange={handleChange} required autoComplete="off" />

              <label className="form-label">Apelido *</label>
              <input type="text" name="nickname" placeholder="Apelido" value={formData.nickname} onChange={handleChange} required autoComplete="off" />

              <label className="form-label">Senha *</label>
              <input type="password" name="password" placeholder="Senha" value={formData.password} onChange={handleChange} required autoComplete="new-password" />

              <label className="form-label">Email *</label>
              <input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleChange} required autoComplete="off" />

              <label className="form-label">Gênero *</label>
              <select name="gender" value={formData.gender} onChange={handleChange} className="formsInside" style={{ height: "auto" }} required>
                <option value="">Selecione</option>
                <option value="Male">Masculino</option>
                <option value="Female">Feminino</option>
                <option value="Other">Outro</option>
              </select>

              <label className="form-label">Telefone</label>
              <PhoneInput
                country={"br"}
                value={formData.telephone}
                onChange={(phone) => setFormData((prev) => ({ ...prev, telephone: phone }))}
                inputProps={{ name: "telephone", placeholder: "Telefone", autoComplete: "off" }}
                enableSearch={true}
                containerStyle={{ marginBottom: "1rem" }}
                buttonStyle={{ display: "none" }}
                inputStyle={{
                  width: "100%",
                  padding: "0.75rem 1rem",
                  border: "2px solid #ccc",
                  borderRadius: "25px",
                  fontSize: "1rem",
                  height: "auto",
                }}
              />

              <label className="form-label">Endereço</label>
              <input type="text" name="address" placeholder="Endereço" value={formData.address} onChange={handleChange} autoComplete="off" />

              <label className="form-label">Cidade</label>
              <input type="text" name="city" placeholder="Cidade" value={formData.city} onChange={handleChange} autoComplete="off" />

              <label className="form-label">Estado</label>
              <input type="text" name="state" placeholder="Estado" value={formData.state} onChange={handleChange} autoComplete="off" />

              <label className="form-label">CEP</label>
              <InputMask mask="99999-999" value={formData.zipcode} onChange={handleChange}>
                {(inputProps) => <input {...inputProps} type="text" name="zipcode" placeholder="CEP" autoComplete="off" />}
              </InputMask>

              <label className="form-label">Aniversário</label>
              <input type="date" name="birthday" value={formData.birthday} onChange={handleChange} autoComplete="off" />

              <label className="form-label">Administrador</label>
              <div className="admin-toggle-wrapper">
                <span className="admin-toggle-label">{formData.admin === "1" ? "Sim" : "Não"}</span>
                <label className="admin-toggle">
                  <input
                    type="checkbox"
                    checked={formData.admin === "1"}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, admin: e.target.checked ? "1" : "0" }))
                    }
                  />
                  <span className="admin-toggle-slider" />
                </label>
              </div>
            </form>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={loading}>
              Cancelar
            </button>
            <button type="submit" form="new-member-form" className="btn btn-primary" disabled={loading}>
              {loading ? "Salvando..." : "Cadastrar"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
